import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Flex, FlexProps } from 'rebass';
import { Input, Label, Select as FormSelect } from '../Form';
import AddressPicker, { AddressType } from '../Address/AddressPicker';
import {
  ContactProfileInput,
  EmailInput,
  PhoneInput,
  profileTypes,
} from './types';
import FormRow from '../Form/FormRow';
import FormColumn from '../Form/FormColumn';
import EditEmails from './EditEmails';
import EditPhones from './EditPhones';

type ContactProfileFormProps = Omit<FlexProps, 'onChange' | 'value'> & {
  onChange?: (profile: ContactProfileInput) => void;
  show?: (keyof ContactProfileInput)[];
  hide?: (keyof ContactProfileInput)[];
  labels?: boolean;
  value?: Partial<ContactProfileInput>;
  singlePhone?: boolean;
  singleEmail?: boolean;
};

export const initContactProfile = (
  value?: Partial<ContactProfileInput>
): ContactProfileInput => {
  return {
    type: value?.type ?? 'INDIVIDUAL',
    name: value?.name ?? '',
    firstName: value?.firstName ?? '',
    lastName: value?.lastName ?? '',
    contactAddressId: value?.contactAddressId ?? null,
    billingAddressId: value?.billingAddressId ?? null,
    phones: !value?.phones?.length
      ? [
          {
            subType: 'UNKNOWN',
            value: '',
            extension: '',
          },
        ]
      : (value?.phones?.map((phone) => ({
          subType: phone.subType,
          value: phone.value,
          extension: phone.extension,
        })) ?? []),
    emails: !value?.emails?.length
      ? [
          {
            subType: 'UNKNOWN',
            value: '',
          },
        ]
      : (value?.emails?.map((email) => ({
          subType: email.subType,
          value: email.value,
        })) ?? []),
  };
};

const ContactProfileForm: React.FC<ContactProfileFormProps> = (props) => {
  const { value, show, hide = [], singlePhone, singleEmail, onChange } = props;
  let { labels } = props;
  labels = labels !== false;

  const profileInput = useMemo<ContactProfileInput>(
    () => initContactProfile(value),
    [value]
  );

  const setProfileInput = useCallback(
    (changeFn: (prevState: ContactProfileInput) => ContactProfileInput) => {
      const result = changeFn(profileInput);
      if (onChange) {
        onChange(result);
      }
    },
    [profileInput]
  );

  // useEffect(() => {
  //   if (onChange) {
  //     console.log('onChange', profileInput)
  //     onChange(profileInput)
  //   }
  // }, [profileInput])

  const updateField = useCallback(
    <F extends keyof ContactProfileInput>(
      field: F,
      value: ContactProfileInput[F]
    ) => {
      setProfileInput((prevState) => ({
        ...prevState,
        [field]: value,
      }));
    },
    [setProfileInput]
  );

  const updateAddress = (
    newAddress: AddressType | null,
    addressType: 'contactAddressId' | 'billingAddressId'
  ) => {
    updateField(addressType, newAddress?.id ?? null);
  };

  const handleContactChange = (
    type: 'phone' | 'email',
    value: PhoneInput | EmailInput,
    index: number
  ) => {
    setProfileInput((prevState) => {
      const key = (type + 's') as 'phones' | 'emails';
      const array = prevState[key].slice();
      array[index] = {
        ...array[index],
        ...value,
      };
      return {
        ...prevState,
        [key]: array,
      };
    });
  };

  const handleTesterProfileObjChange = (e: any) => {
    const { name, value } = e.target;
    updateField(name, value);
  };

  const handleAddPhoneOrEmail = (e: any) => {
    setProfileInput((prevState) => {
      const name: 'phones' | 'emails' = e.target.name;
      const newValue: PhoneInput | EmailInput = {
        subType: 'UNKNOWN',
        value: '',
      };
      return {
        ...prevState,
        [name]: [...prevState[name], newValue],
      };
    });
  };

  const handleDeletePhoneOrEmail = (idx: number, type: 'phones' | 'emails') => {
    setProfileInput((prevState) => {
      const newArray = profileInput[type].slice();
      newArray.splice(idx, 1);
      return {
        ...prevState,
        [type]: newArray,
      };
    });
  };

  const showField = (field: keyof ContactProfileInput) =>
    (!show || show.includes(field)) && !hide?.includes(field);

  return (
    <Flex
      {...({ ...props, onChange: undefined } as any)}
      flexDirection="column"
    >
      {showField('type') && (
        <FormRow>
          <FormColumn>
            {labels && <Label>Type</Label>}
            <FormSelect
              name="type"
              onChange={handleTesterProfileObjChange}
              value={profileInput.type}
              width="100%"
            >
              <option value="" disabled hidden>
                Select Type
              </option>
              {profileTypes.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </FormSelect>
          </FormColumn>
        </FormRow>
      )}
      {showField('name') && (
        <>
          {profileInput.type === 'COMPANY' ? (
            <FormColumn>
              {labels && <Label>Name</Label>}
              <Input
                width="100%"
                type="text"
                value={profileInput.name || ''}
                onChange={handleTesterProfileObjChange}
                name="name"
                placeholder="Name"
                autoComplete="off"
              />
            </FormColumn>
          ) : (
            <FormRow>
              <FormColumn>
                {labels && <Label>First Name</Label>}
                <Input
                  width="100%"
                  type="text"
                  value={profileInput.firstName || ''}
                  onChange={handleTesterProfileObjChange}
                  name="firstName"
                  placeholder="First"
                  autoComplete="off"
                />
              </FormColumn>
              <FormColumn>
                {labels && <Label>Last Name</Label>}
                <Input
                  width="100%"
                  type="text"
                  value={profileInput.lastName || ''}
                  onChange={handleTesterProfileObjChange}
                  name="lastName"
                  placeholder="Last"
                  autoComplete="off"
                />
              </FormColumn>
            </FormRow>
          )}
        </>
      )}
      {showField('contactAddressId') && (
        <FormColumn>
          {labels && <Label>Mailing Address</Label>}
          <AddressPicker
            addressId={profileInput.contactAddressId}
            onChange={(newAddress) =>
              updateAddress(newAddress, 'contactAddressId')
            }
            allowCreate
          />
        </FormColumn>
      )}
      {showField('billingAddressId') && (
        <FormColumn>
          {labels && <Label>Billing Address</Label>}
          <AddressPicker
            addressId={profileInput.billingAddressId}
            onChange={(newAddress) =>
              updateAddress(newAddress, 'billingAddressId')
            }
            allowCreate
          />
        </FormColumn>
      )}
      {(showField('phones') || showField('emails')) && (
        <FormRow>
          {showField('phones') && (
            <FormColumn>
              <EditPhones
                phones={profileInput.phones}
                labels={labels}
                singlePhone={singlePhone}
                handleContactChange={handleContactChange}
                handleAddPhoneOrEmail={handleAddPhoneOrEmail}
                handleDeletePhoneOrEmail={handleDeletePhoneOrEmail}
              />
            </FormColumn>
          )}

          {showField('emails') && (
            <FormColumn>
              <EditEmails
                emails={profileInput.emails}
                labels={labels}
                singleEmail={singleEmail}
                handleContactChange={handleContactChange}
                handleAddPhoneOrEmail={handleAddPhoneOrEmail}
                handleDeletePhoneOrEmail={handleDeletePhoneOrEmail}
              />
            </FormColumn>
          )}
        </FormRow>
      )}
    </Flex>
  );
};

ContactProfileForm.propTypes = {
  onChange: PropTypes.any,
  value: PropTypes.any,
  hide: PropTypes.any,
};

export default ContactProfileForm;
